import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Landmark, Lock, Users as UsersIcon, Wallet } from 'lucide-react';
import { Button, Card, Pill, useToast } from '@nako/ui';
import { buildProfitLoss, formatMoney, monthlyPaye } from '@nako/core';
import type { Account, JournalEntry, JournalLine } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import {
  fetchAccountMap,
  localYmd,
  postJournalEntry,
  r2,
  type PostLine,
} from '@/lib/journal-postings';

interface PayeEmployee {
  id: string;
  name: string;
  national_id: string | null;
  basic_salary: number;
  allowances: { transport?: number; housing?: number; other?: number };
  deductions: { pension_employee?: number; medical_aid?: number; loan?: number; other?: number };
  is_active: boolean;
}

interface PayeRow {
  name: string;
  national_id: string;
  gross: number;
  pension: number;
  taxable: number;
  paye: number;
}

const monthName = (m: number) =>
  ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m] ?? '';

interface Vat002Result {
  business_name: string;
  vat_number: string | null;
  period_start: string;
  period_end: string;
  output_vat: number;
  input_vat: number;
  net_payable: number;
  generated_at: string;
}

export default function BURS() {
  const business = useAuthStore((s) => s.business)!;
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const toast = useToast();
  const isPaid = business.tier !== 'free';
  const isVat = business.vat_registered;

  const [from, setFrom] = useState(firstOfQuarter());
  const [to, setTo] = useState(lastOfQuarter());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vat002Result | null>(null);
  const [csv, setCsv] = useState<string | null>(null);

  async function generate() {
    if (!isPaid) {
      toast.show(`Upgrade to Smart to export ${taxAuth} forms.`, { tone: 'warning' });
      return;
    }
    if (!isVat) {
      toast.show('Mark your business VAT-registered in Settings first.', { tone: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-burs-vat-002', {
        body: { business_id: business.id, period_start: from, period_end: to },
      });
      if (error) throw error;
      setResult(data.result);
      setCsv(data.csv);
      toast.show('VAT 002 generated.', { tone: 'success' });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Failed', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function downloadCsv() {
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vat002_${from}_to_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <h1 className="font-display mb-4 flex items-center gap-2 text-2xl">
        <FileText size={22} /> {taxAuth} Tax Pack
      </h1>

      <div className="space-y-3">
        <TaxPaymentCenter businessId={business.id} isVat={isVat} />

        <Card>
          <div className="mb-3 flex items-start justify-between">
            <div>
              <h2 className="font-display text-lg">VAT 002 Return</h2>
              <p className="text-muted text-sm">Quarterly VAT for {taxAuth}</p>
            </div>
            {isPaid ? (
              <Pill tone="green">Available</Pill>
            ) : (
              <Pill tone="neutral">
                <Lock size={10} className="mr-1 inline" /> Smart+
              </Pill>
            )}
          </div>

          <div className="mb-3 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="min-h-tap rounded-btn border-line border bg-white px-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="min-h-tap rounded-btn border-line border bg-white px-2"
              />
            </label>
          </div>

          <Button full onClick={generate} loading={loading} disabled={!isPaid || !isVat}>
            Generate VAT 002
          </Button>

          {result && (
            <div className="border-line mt-4 space-y-2 border-t pt-4 text-sm">
              <Row label="Business" value={result.business_name} />
              <Row label="VAT number" value={result.vat_number ?? '—'} />
              <Row label="Period" value={`${result.period_start} → ${result.period_end}`} />
              <Row label="Output VAT" value={formatMoney(result.output_vat, regionCode)} bold />
              <Row label="Input VAT" value={formatMoney(result.input_vat, regionCode)} bold />
              <div className="border-line font-display flex justify-between border-t pt-2 text-base">
                <span>Net payable</span>
                <span
                  className={`tnum font-mono ${result.net_payable >= 0 ? 'text-red' : 'text-green'}`}
                >
                  {formatMoney(result.net_payable, regionCode)}
                </span>
              </div>
              <Button
                onClick={downloadCsv}
                leftIcon={<Download size={16} />}
                variant="secondary"
                full
              >
                Download CSV
              </Button>
            </div>
          )}
        </Card>

        <PayeCertificate
          businessId={business.id}
          businessName={business.name}
          tin={business.registration_no ?? null}
          isPaid={isPaid}
        />

        <IncomeTaxReturn
          businessId={business.id}
          businessName={business.name}
          tin={business.registration_no ?? null}
          fiscalStartMonth={business.fiscal_year_start_month}
          isPaid={isPaid}
        />
      </div>
    </main>
  );
}

// ───── Pay tax (record settlement of VAT / PAYE liabilities) ────────
//
// The returns above tell you what's owed; this records that you've PAID it,
// clearing the liability in the ledger. Without it, VAT Output (2100) and
// PAYE Payable (2200) accumulate forever and never reconcile to the bank.
//   VAT settlement: Dr VAT Output (2100), Cr VAT Input (2110), net to Cash.
//   PAYE payment:   Dr PAYE Payable (2200), Cr Cash (1000).
function TaxPaymentCenter({ businessId, isVat }: { businessId: string; isVat: boolean }) {
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const session = useAuthStore((s) => s.session);
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'vat' | 'paye' | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [lines, setLines] = useState<JournalLine[]>([]);

  async function reload() {
    setLoading(true);
    // Fetch entries first so lines can be filtered by entry_id — defence in
    // depth: RLS scopes this server-side, but the explicit filter prevents a
    // single misconfigured policy from leaking another tenant's ledger.
    const [a, e] = await Promise.all([
      supabase.from('accounts').select('*').eq('business_id', businessId),
      supabase.from('journal_entries').select('*').eq('business_id', businessId),
    ]);
    const entryIds = (e.data ?? []).map((x) => x.id);
    const l =
      entryIds.length === 0
        ? { data: [] }
        : await supabase.from('journal_lines').select('*').in('entry_id', entryIds);
    setAccounts((a.data ?? []) as Account[]);
    setEntries((e.data ?? []) as JournalEntry[]);
    setLines((l.data ?? []) as JournalLine[]);
    setLoading(false);
  }
  useEffect(() => {
    void reload();
  }, [businessId]);

  const balances = useMemo(() => {
    const idToCode = new Map<string, string>();
    for (const a of accounts) idToCode.set(a.id, a.gl_code);
    const postedIds = new Set(entries.filter((e) => e.is_posted).map((e) => e.id));
    const acc: Record<string, { debit: number; credit: number }> = {};
    for (const l of lines) {
      if (!postedIds.has(l.entry_id)) continue;
      const code = idToCode.get(l.account_id);
      if (!code) continue;
      const cur = acc[code] ?? { debit: 0, credit: 0 };
      cur.debit += Number(l.debit) || 0;
      cur.credit += Number(l.credit) || 0;
      acc[code] = cur;
    }
    const cr = (code: string) => r2((acc[code]?.credit ?? 0) - (acc[code]?.debit ?? 0));
    const dr = (code: string) => r2((acc[code]?.debit ?? 0) - (acc[code]?.credit ?? 0));
    const vatOutput = cr('2100');
    const vatInput = dr('2110');
    return { vatOutput, vatInput, vatNet: r2(vatOutput - vatInput), paye: cr('2200') };
  }, [accounts, entries, lines]);

  async function payVat() {
    setBusy('vat');
    try {
      const accts = await fetchAccountMap(businessId);
      const vatOut = accts.get('2100');
      const vatIn = accts.get('2110');
      const cash = accts.get('1000');
      if (!vatOut || !vatIn || !cash) {
        toast.show('Missing VAT / cash accounts.', { tone: 'error' });
        return;
      }
      const out = balances.vatOutput;
      const inp = balances.vatInput;
      const net = r2(out - inp);
      if (out <= 0 && inp <= 0) {
        toast.show('No VAT balance to settle.', { tone: 'warning' });
        return;
      }
      const date = localYmd();
      const postLines: PostLine[] = [];
      if (out > 0)
        postLines.push({
          account_id: vatOut,
          debit: out,
          credit: 0,
          description: 'Clear VAT output payable',
        });
      if (inp > 0)
        postLines.push({
          account_id: vatIn,
          debit: 0,
          credit: inp,
          description: 'Clear VAT input receivable',
        });
      if (net > 0)
        postLines.push({
          account_id: cash,
          debit: 0,
          credit: net,
          description: `VAT paid to ${taxAuth}`,
        });
      else if (net < 0)
        postLines.push({
          account_id: cash,
          debit: -net,
          credit: 0,
          description: `VAT refund from ${taxAuth}`,
        });
      const posted = await postJournalEntry({
        businessId,
        date,
        reference: `VAT settlement — ${date}`,
        description: `VAT 002 settlement with ${taxAuth}`,
        source: 'bank',
        sourceId: null,
        createdBy: session?.user.id ?? null,
        lines: postLines,
      });
      toast.show(
        posted
          ? `VAT settled — ${net >= 0 ? 'paid' : 'refund'} ${formatMoney(Math.abs(net), regionCode)}.`
          : 'A VAT settlement was already recorded today.',
        { tone: posted ? 'success' : 'warning' },
      );
      await reload();
    } finally {
      setBusy(null);
    }
  }

  async function payPaye() {
    setBusy('paye');
    try {
      const accts = await fetchAccountMap(businessId);
      const payeAcc = accts.get('2200');
      const cash = accts.get('1000');
      if (!payeAcc || !cash) {
        toast.show('Missing PAYE / cash accounts.', { tone: 'error' });
        return;
      }
      const due = balances.paye;
      if (due <= 0) {
        toast.show('No PAYE balance to pay.', { tone: 'warning' });
        return;
      }
      const date = localYmd();
      const posted = await postJournalEntry({
        businessId,
        date,
        reference: `PAYE payment — ${date}`,
        description: `PAYE remitted to ${taxAuth}`,
        source: 'bank',
        sourceId: null,
        createdBy: session?.user.id ?? null,
        lines: [
          { account_id: payeAcc, debit: due, credit: 0, description: 'Clear PAYE payable' },
          { account_id: cash, debit: 0, credit: due, description: `PAYE paid to ${taxAuth}` },
        ],
      });
      toast.show(
        posted
          ? `PAYE paid — ${formatMoney(due, regionCode)}.`
          : 'A PAYE payment was already recorded today.',
        { tone: posted ? 'success' : 'warning' },
      );
      await reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg">
            <Wallet size={18} /> Pay tax to {taxAuth}
          </h2>
          <p className="text-muted text-sm">
            Record what you&apos;ve paid — clears the liability in your ledger
          </p>
        </div>
      </div>
      {loading ? (
        <p className="text-muted text-sm">Loading balances…</p>
      ) : (
        <div className="space-y-4">
          {isVat && (
            <div className="border-line space-y-1 border-t pt-3 text-sm">
              <Row label="VAT output payable" value={formatMoney(balances.vatOutput, regionCode)} />
              <Row
                label="Less: VAT input receivable"
                value={formatMoney(balances.vatInput, regionCode)}
              />
              <div className="border-line flex justify-between border-t pt-2 font-medium">
                <span>{balances.vatNet >= 0 ? `Net VAT due to ${taxAuth}` : 'VAT refund due'}</span>
                <span
                  className={`tnum font-mono ${balances.vatNet >= 0 ? 'text-red' : 'text-green'}`}
                >
                  {formatMoney(Math.abs(balances.vatNet), regionCode)}
                </span>
              </div>
              <Button
                full
                className="mt-2"
                onClick={() => void payVat()}
                loading={busy === 'vat'}
                disabled={balances.vatOutput <= 0 && balances.vatInput <= 0}
              >
                Record VAT settlement
              </Button>
              {balances.vatOutput <= 0 && balances.vatInput <= 0 && (
                <p className="text-muted mt-1 text-[11px]">
                  No VAT activity yet — record sales/purchases to accrue VAT, then this button
                  activates.
                </p>
              )}
            </div>
          )}
          <div className="border-line space-y-1 border-t pt-3 text-sm">
            <div className="flex justify-between font-medium">
              <span>PAYE payable to {taxAuth}</span>
              <span className="tnum text-red font-mono">
                {formatMoney(balances.paye, regionCode)}
              </span>
            </div>
            <Button
              full
              variant="secondary"
              className="mt-2"
              onClick={() => void payPaye()}
              loading={busy === 'paye'}
              disabled={balances.paye <= 0}
            >
              Record PAYE payment
            </Button>
            {balances.paye <= 0 && (
              <p className="text-muted mt-1 text-[11px]">
                Nothing to remit — PAYE is settled at {formatMoney(0, regionCode)}. Run payroll to
                accrue PAYE, then this button activates.
              </p>
            )}
          </div>
          <p className="text-muted text-[11px]">
            Posts a bank payment dated today, clearing the liability against Cash at Bank (1000).
          </p>
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted">{label}</span>
      <span className={bold ? 'tnum font-mono font-medium' : 'tnum font-mono'}>{value}</span>
    </div>
  );
}

function PayeCertificate({
  businessId,
  businessName,
  tin,
  isPaid,
}: {
  businessId: string;
  businessName: string;
  tin: string | null;
  isPaid: boolean;
}) {
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [employees, setEmployees] = useState<PayeEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('employees')
        .select('id, name, national_id, basic_salary, allowances, deductions, is_active')
        .eq('business_id', businessId)
        .order('name');
      setEmployees((data ?? []) as PayeEmployee[]);
      setLoading(false);
    })();
  }, [businessId]);

  const rows: PayeRow[] = useMemo(() => {
    return employees
      .filter((e) => e.is_active)
      .map((e) => {
        const transport = e.allowances?.transport ?? 0;
        const housing = e.allowances?.housing ?? 0;
        const other = e.allowances?.other ?? 0;
        const gross = (e.basic_salary || 0) + transport + housing + other;
        const pension = e.deductions?.pension_employee ?? 0;
        const taxable = Math.max(0, gross - pension);
        const paye = monthlyPaye(taxable);
        return {
          name: e.name,
          national_id: e.national_id ?? '',
          gross: round2(gross),
          pension: round2(pension),
          taxable: round2(taxable),
          paye: round2(paye),
        };
      });
  }, [employees]);

  const totals = useMemo(() => {
    let gross = 0;
    let paye = 0;
    for (const r of rows) {
      gross += r.gross;
      paye += r.paye;
    }
    return { gross, paye };
  }, [rows]);

  function downloadCertificate() {
    if (!isPaid) {
      toast.show(`Upgrade to Smart to download ${taxAuth} certificates.`, { tone: 'warning' });
      return;
    }
    if (rows.length === 0) {
      toast.show('No active employees in payroll.', { tone: 'warning' });
      return;
    }
    const period = `${monthName(month)} ${year}`;
    const lines: string[] = [];
    lines.push('PAYE Certificate (Monthly Withholding Schedule)');
    lines.push(`Employer,${q(businessName)}`);
    lines.push(`${taxAuth} TIN / Reg No,${q(tin ?? 'NOT SET')}`);
    lines.push(`Period,${period}`);
    lines.push(`Generated,${new Date().toISOString().slice(0, 10)}`);
    lines.push('');
    lines.push(
      'Employee,National ID,Gross Earnings,Pension Deduction,Taxable Income,PAYE Withheld',
    );
    for (const r of rows) {
      lines.push([q(r.name), q(r.national_id), r.gross, r.pension, r.taxable, r.paye].join(','));
    }
    lines.push('');
    lines.push(`Totals,,${round2(totals.gross)},,,${round2(totals.paye)}`);

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paye_${year}_${monthName(month)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.show('PAYE certificate downloaded ✓', { tone: 'success' });
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg">
            <UsersIcon size={18} /> PAYE Certificate
          </h2>
          <p className="text-muted text-sm">Monthly PAYE schedule for {taxAuth}</p>
        </div>
        {isPaid ? (
          <Pill tone="green">Available</Pill>
        ) : (
          <Pill tone="neutral">
            <Lock size={10} className="mr-1 inline" /> Smart+
          </Pill>
        )}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs">
          Month
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="min-h-tap rounded-btn border-line border bg-white px-2"
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {monthName(i)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Year
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="min-h-tap rounded-btn border-line border bg-white px-2 font-mono"
          />
        </label>
      </div>

      {loading ? (
        <p className="text-muted text-sm">Loading employees…</p>
      ) : rows.length === 0 ? (
        <p className="text-muted py-3 text-sm">
          No active employees on payroll. Add employees in the Payroll module first.
        </p>
      ) : (
        <>
          <div className="border-line space-y-1 border-t pt-3 text-sm">
            <Row label="Employer" value={businessName} />
            <Row label={`${taxAuth} TIN / Reg No`} value={tin ?? '— not set —'} />
            <Row label="Period" value={`${monthName(month)} ${year}`} />
            <Row label="Active employees" value={String(rows.length)} />
            <Row label="Total gross" value={formatMoney(totals.gross, regionCode)} bold />
            <div className="border-line font-display flex justify-between border-t pt-2 text-base">
              <span>Total PAYE withheld</span>
              <span className="tnum text-red font-mono">
                {formatMoney(totals.paye, regionCode)}
              </span>
            </div>
          </div>

          <Button
            onClick={downloadCertificate}
            leftIcon={<Download size={16} />}
            variant="secondary"
            full
            className="mt-4"
            disabled={!isPaid}
          >
            Download PAYE certificate (CSV)
          </Button>
        </>
      )}
    </Card>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ───── Income Tax IT14 (annual return) ──────────────────────────────
//
// Botswana corporate (resident company) tax: flat 22% on chargeable income
// for the year of assessment. Reduced rate of 15% applies to manufacturing
// and IFSC operators — surfaced as a toggle here so the practice can pick
// the right rate without us having to know their licence status.
//
// Uses buildProfitLoss across the business's fiscal year to compute net
// profit before tax, then applies the chosen rate. CSV download produces
// the figures in the order BURS expects on the IT14 form.

const TAX_RATE_OPTIONS = [
  { id: 'standard', label: 'Standard 22% (most companies)', rate: 0.22 },
  { id: 'manufacturing', label: 'Manufacturing 15%', rate: 0.15 },
  { id: 'ifsc', label: 'IFSC / approved 15%', rate: 0.15 },
] as const;

function IncomeTaxReturn({
  businessId,
  businessName,
  tin,
  fiscalStartMonth,
  isPaid,
}: {
  businessId: string;
  businessName: string;
  tin: string | null;
  fiscalStartMonth: number;
  isPaid: boolean;
}) {
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const toast = useToast();
  const today = new Date();
  // Default to the most recently completed FY. If we are inside FY2026 (which
  // ends in month fiscalStartMonth-1), the most recent completed year is
  // FY2026 if today >= fiscalStartMonth, otherwise FY2025.
  const defaultYear =
    today.getMonth() + 1 >= fiscalStartMonth ? today.getFullYear() : today.getFullYear() - 1;

  const [year, setYear] = useState(defaultYear);
  const [rateId, setRateId] = useState<(typeof TAX_RATE_OPTIONS)[number]['id']>('standard');
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [lines, setLines] = useState<JournalLine[]>([]);
  const [payeWithheld, setPayeWithheld] = useState(0);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      // Entries first, then lines filtered by entry_id (defence in depth —
      // see the reload() comment in the settlement card above).
      const [a, e] = await Promise.all([
        supabase.from('accounts').select('*').eq('business_id', businessId),
        supabase.from('journal_entries').select('*').eq('business_id', businessId),
      ]);
      const entryIds = (e.data ?? []).map((x) => x.id);
      const l =
        entryIds.length === 0
          ? { data: [] }
          : await supabase.from('journal_lines').select('*').in('entry_id', entryIds);
      setAccounts((a.data ?? []) as Account[]);
      setEntries((e.data ?? []) as JournalEntry[]);
      setLines((l.data ?? []) as JournalLine[]);
      setLoading(false);
    })();
  }, [businessId]);

  const period = useMemo(() => {
    // FY runs from fiscalStartMonth of (year-1) through fiscalStartMonth-1 of year.
    // Botswana standard FY is July-June, so fiscalStartMonth=7 → FY2026 = 1 Jul 2025 → 30 Jun 2026.
    const startMonthIdx = (fiscalStartMonth - 1 + 12) % 12;
    const startYear = year - 1;
    const start = new Date(startYear, startMonthIdx, 1);
    const end = new Date(year, startMonthIdx, 0); // last day of month before start
    return { start, end };
  }, [year, fiscalStartMonth]);

  const pl = useMemo(() => {
    if (loading) return null;
    return buildProfitLoss({ accounts, entries, lines }, period.start, period.end);
  }, [loading, accounts, entries, lines, period]);

  // Pull total PAYE already remitted via the payroll module across the year.
  useEffect(() => {
    if (loading) return;
    // localYmd, NOT toISOString: these are local dates — the UTC shift would
    // move both FY boundaries back a day (include prior-FY month-end, drop
    // the FY's actual last day).
    const startIso = localYmd(period.start);
    const endIso = localYmd(period.end);
    const payeAcct = accounts.find((a) => a.gl_code === '2200');
    if (!payeAcct) {
      setPayeWithheld(0);
      return;
    }
    const entryById = new Map(entries.map((e) => [e.id, e]));
    let total = 0;
    for (const line of lines) {
      if (line.account_id !== payeAcct.id) continue;
      const ent = entryById.get(line.entry_id);
      if (!ent) continue;
      if (ent.entry_date < startIso || ent.entry_date > endIso) continue;
      total += (Number(line.credit) || 0) - (Number(line.debit) || 0);
    }
    setPayeWithheld(total);
  }, [loading, accounts, entries, lines, period]);

  const rate = TAX_RATE_OPTIONS.find((r) => r.id === rateId)?.rate ?? 0.22;
  const chargeable = pl ? Math.max(0, pl.netProfit) : 0;
  const taxDue = round2(chargeable * rate);
  const balance = round2(taxDue - payeWithheld);

  function downloadIt14() {
    if (!isPaid) {
      toast.show(`Upgrade to Smart to download ${taxAuth} forms.`, { tone: 'warning' });
      return;
    }
    if (!pl) return;
    const fy = `FY${year}`;
    const periodLabel = `${localYmd(period.start)} to ${localYmd(period.end)}`;

    const lines: string[] = [];
    lines.push('Income Tax Return IT14 (Annual)');
    lines.push(`Taxpayer,${q(businessName)}`);
    lines.push(`${taxAuth} TIN / Reg No,${q(tin ?? 'NOT SET')}`);
    lines.push(`Year of assessment,${fy}`);
    lines.push(`Period,${periodLabel}`);
    lines.push(`Generated,${new Date().toISOString().slice(0, 10)}`);
    lines.push('');
    lines.push('Section A — Income Statement Summary');
    lines.push(`Total income,${round2(pl.totalIncome)}`);
    lines.push(`Total expenses,${round2(pl.totalExpense)}`);
    lines.push(`Net profit before tax,${round2(pl.netProfit)}`);
    lines.push('');
    lines.push('Section B — Tax Computation');
    lines.push(
      `Applicable rate,${(TAX_RATE_OPTIONS.find((r) => r.id === rateId)?.label ?? '').replace(',', '')}`,
    );
    lines.push(`Chargeable income,${round2(chargeable)}`);
    lines.push(`Tax at ${(rate * 100).toFixed(0)}%,${taxDue}`);
    lines.push(`Less: PAYE already withheld,${round2(payeWithheld)}`);
    lines.push(
      `Balance ${balance >= 0 ? `PAYABLE TO ${taxAuth}` : `REFUNDABLE FROM ${taxAuth}`},${Math.abs(balance)}`,
    );

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `it14_${fy}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.show('IT14 downloaded ✓', { tone: 'success' });
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg">
            <Landmark size={18} /> Income Tax IT14
          </h2>
          <p className="text-muted text-sm">Annual return for {taxAuth}</p>
        </div>
        {isPaid ? (
          <Pill tone="green">Available</Pill>
        ) : (
          <Pill tone="neutral">
            <Lock size={10} className="mr-1 inline" /> Smart+
          </Pill>
        )}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs">
          Year of assessment
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="min-h-tap rounded-btn border-line border bg-white px-2 font-mono"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Tax rate
          <select
            value={rateId}
            onChange={(e) => setRateId(e.target.value as (typeof TAX_RATE_OPTIONS)[number]['id'])}
            className="min-h-tap rounded-btn border-line border bg-white px-2"
          >
            {TAX_RATE_OPTIONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-muted text-sm">Loading ledger…</p>
      ) : !pl ? (
        <p className="text-muted text-sm">No data.</p>
      ) : (
        <>
          <div className="border-line space-y-1 border-t pt-3 text-sm">
            <Row label="Period" value={`${localYmd(period.start)} → ${localYmd(period.end)}`} />
            <Row label="Total income" value={formatMoney(pl.totalIncome, regionCode)} />
            <Row label="Total expenses" value={formatMoney(pl.totalExpense, regionCode)} />
            <div className="border-line flex justify-between border-t pt-2">
              <span className="font-medium">Net profit before tax</span>
              <span
                className={`tnum font-mono font-medium ${pl.netProfit >= 0 ? 'text-ink' : 'text-red'}`}
              >
                {formatMoney(pl.netProfit, regionCode)}
              </span>
            </div>
            <Row label="Chargeable income" value={formatMoney(chargeable, regionCode)} />
            <Row
              label={`Tax at ${(rate * 100).toFixed(0)}%`}
              value={formatMoney(taxDue, regionCode)}
              bold
            />
            <Row
              label="Less: PAYE already withheld"
              value={formatMoney(payeWithheld, regionCode)}
            />
            <div className="border-line font-display flex justify-between border-t pt-2 text-base">
              <span>
                {balance >= 0 ? `Balance payable to ${taxAuth}` : `Refund from ${taxAuth}`}
              </span>
              <span className={`tnum font-mono ${balance >= 0 ? 'text-red' : 'text-green'}`}>
                {formatMoney(Math.abs(balance), regionCode)}
              </span>
            </div>
          </div>

          <Button
            onClick={downloadIt14}
            leftIcon={<Download size={16} />}
            variant="secondary"
            full
            className="mt-4"
            disabled={!isPaid}
          >
            Download IT14 (CSV)
          </Button>

          <p className="text-muted mt-3 text-xs">
            Heads-up: this is a working draft. Have your accountant confirm before filing — most tax
            returns also need add-backs for non-deductible expenses (entertainment, donations over
            the cap, depreciation timing, etc.) which we don&apos;t auto-detect yet.
          </p>
        </>
      )}
    </Card>
  );
}

function q(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function firstOfQuarter(): string {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  // localYmd, NOT toISOString — the UTC shift (we're UTC+2) would return the
  // last day of the PREVIOUS quarter / the day before the quarter actually ends.
  return localYmd(new Date(d.getFullYear(), q * 3, 1));
}
function lastOfQuarter(): string {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  return localYmd(new Date(d.getFullYear(), q * 3 + 3, 0));
}
